import { type Icon } from "@/components/Base/Lucide/Lucide.vue";
import { defineStore } from "pinia";
import { UserRoleEnum } from '@/enums'
import { useAuthStore } from "@/stores/app/AuthStore";

export interface Menu {
  icon: Icon;
  title: string;
  badge?: number;
  path?: string;
  onClick?: string;
  pageName?: string;
  subMenu?: Menu[];
  ignore?: boolean;
  roles?: UserRoleEnum;
}

export interface SimpleMenu {
  type: string;
  title: string;
  roles?: UserRoleEnum;
  onClick?: string;
}

export interface MenuState {
  baseMenu: Array<Menu | SimpleMenu>;
  dynamicMenu: Array<Menu>;
  activeMobileMenu: boolean;
}

export const useMenuStore = defineStore("menu", {
  state: (): MenuState => ({
    activeMobileMenu: false,
    baseMenu: [
      // ============================================
      // TOURNAMENT - For participants
      // ============================================
      {
        type: "heading",
        title: "Tournament",
        roles: [UserRoleEnum.STANDARDUSER, UserRoleEnum.ADMIN, UserRoleEnum.TOURNAMENTMANAGER, UserRoleEnum.SUPERADMIN]
      },
      // Dynamic menu items (View Your Team/Teams) will be inserted here before "Register Your Team"
      {
        icon: "Trophy",
        pageName: "team-registration",
        title: "Register Your Team",
        roles: [UserRoleEnum.STANDARDUSER, UserRoleEnum.ADMIN, UserRoleEnum.TOURNAMENTMANAGER, UserRoleEnum.SUPERADMIN]
      },
      {
        icon: "Component",
        path: '/team/invitations',
        pageName: "",
        title: "Your Invitations",
        roles: [UserRoleEnum.STANDARDUSER, UserRoleEnum.ADMIN, UserRoleEnum.TOURNAMENTMANAGER, UserRoleEnum.SUPERADMIN]
      },

      // ============================================
      // TOURNAMENT MANAGEMENT - For managers/admins
      // ============================================
      {
        type: "heading",
        title: "Tournament Management",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TOURNAMENTMANAGER]
      },
      {
        icon: "Plus",
        pageName: "create-event",
        title: "Create Event",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.SUPERADMIN, UserRoleEnum.TOURNAMENTMANAGER],
      },
      {
        icon: "Award",
        pageName: "view-tournaments",
        title: "Tournaments",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TOURNAMENTMANAGER],
      },
      {
        icon: "Star",
        pageName: "view-events",
        title: "Events",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TOURNAMENTMANAGER],
      },
      {
        icon: "UsersRound",
        pageName: "view-active-tournament-teams",
        title: "Teams",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TOURNAMENTMANAGER],
      },
      {
        icon: "UserRoundCheckIcon",
        pageName: "view-active-players",
        title: "Players",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TOURNAMENTMANAGER],
      },

      // ============================================
      // TOURNAMENT OPERATIONS - For staff during events
      // ============================================
      {
        type: "heading",
        title: "Tournament Operations",
        roles: [UserRoleEnum.STAFF, UserRoleEnum.PARTNER, UserRoleEnum.TOURNAMENTMANAGER, UserRoleEnum.ADMIN, UserRoleEnum.TEAMCHECKINOPERATOR]
      },
      {
        icon: "TicketIcon",
        pageName: "team-check-in",
        title: "Team Check In",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.STAFF, UserRoleEnum.TOURNAMENTMANAGER, UserRoleEnum.TEAMCHECKINOPERATOR]
      },
      {
        icon: "ShirtIcon",
        pageName: "team-uniform",
        title: "Handout Uniform",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.PARTNER]
      },

      {
        type: "check-in",
        title: "Check-In",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.CHECKINOPERATOR, UserRoleEnum.TICKETINGMANAGER]
      },
      {
        icon: "Scan",
        path: '/check-in',
        pageName: "check-in-scanner",
        title: "Scanner",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.CHECKINOPERATOR, UserRoleEnum.TICKETINGMANAGER]
      },
      {
        icon: "BarChart3",
        path: '/check-in/stats',
        pageName: "check-in-stats",
        title: "Statistics",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TICKETINGMANAGER]
      },

      // ============================================
      // TICKETING - Event ticketing system
      // ============================================
      {
        type: "heading",
        title: "Ticketing",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TICKETINGADMIN, UserRoleEnum.ORDERMANAGER]
      },
      {
        icon: "PlusCircle",
        path: '/ticketing/events/new',
        pageName: "",
        title: "Create Ticketing Event",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TICKETINGADMIN]
      },
      {
        icon: "Ticket",
        path: '/ticketing/events',
        pageName: "",
        title: "Ticketing Events",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TICKETINGADMIN]
      },
      {
        icon: "Clipboard",
        path: '/ticketing/orders',
        pageName: "",
        title: "Orders",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.TICKETINGADMIN, UserRoleEnum.ORDERMANAGER]
      },

      // ============================================
      // ACTIVATIONS - Marketing activations
      // ============================================
      {
        type: "heading",
        title: "Activations",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.ACTIVATIONADMIN]
      },
      {
        icon: "PlusCircle",
        path: '/activations/new',
        pageName: "",
        title: "Create Activation",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.ACTIVATIONADMIN]
      },
      {
        icon: "Zap",
        path: '/activations',
        pageName: "",
        title: "All Activations",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.ACTIVATIONADMIN]
      },
      {
        icon: "Users",
        path: '/activations/entries',
        pageName: "",
        title: "Entries",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.ACTIVATIONADMIN]
      },

      // ============================================
      // SYSTEM ADMINISTRATION - System-wide admin tasks
      // ============================================
      {
        type: "heading",
        title: "System Administration",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.SUPERADMIN],
      },
      {
        icon: "Mail",
        pageName: "email-templates",
        title: "Email Templates",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.SUPERADMIN],
      },
      {
        icon: "Users",
        pageName: "admin-users",
        title: "User Management",
        roles: [UserRoleEnum.ADMIN, UserRoleEnum.SUPERADMIN],
      },

      // ============================================
      // HELP - Support and assistance
      // ============================================
      {
        type: "heading",
        title: "Help"
      },
      {
        icon: "HelpCircleIcon",
        title: "Contact Us",
        pageName: "",
        onClick: "openContactPopup",
      },
      // {
      //   icon: "Users",
      //   pageName: "",
      //   title: "Users",
      //   roles: [UserRoleEnum.ADMIN, UserRoleEnum.SUPERADMIN],
      // },
      // {
      //   type: "heading",
      //   title: "Nike",
      //   roles: [UserRoleEnum.TOURNAMENTMANAGER, UserRoleEnum.PARTNER],
      // },
      // {
      //   icon: "Trophy",
      //   pageName: "",
      //   title: "Uniform Allocation",
      //   roles: [UserRoleEnum.TOURNAMENTMANAGER, UserRoleEnum.PARTNER],
      // },
      // {
      //   type: "heading",
      //   title: "Sponsors"
      // },
      // {
      //   icon: "BarChart",
      //   pageName: "analytics",
      //   title: "Analytics",
      // },
      // "APPS",
      // {
      //   icon: "GanttChartSquare",
      //   pageName: "inbox",
      //   title: "Inbox",
      //   badge: 4,
      // },
      // {
      //   icon: "MousePointerSquare",
      //   pageName: "dashboard-overview-6",
      //   title: "Cafe",
      // },      
      // {
      //   icon: "PanelRightClose",
      //   pageName: "file-manager-list",
      //   title: "File Manager List",
      // },
      // {
      //   icon: "PanelTopClose",
      //   pageName: "file-manager-grid",
      //   title: "File Manager Grid",
      // },
      // {
      //   icon: "PanelTopClose",
      //   pageName: "point-of-sale",
      //   title: "Point of Sale",
      // },
      // {
      //   icon: "MailOpen",
      //   pageName: "chat",
      //   title: "Chat",
      // },
      // {
      //   icon: "CalendarRange",
      //   pageName: "calendar",
      //   title: "Calendar",
      // },
      // "UI WIDGETS",
      // {
      //   icon: "Album",
      //   pageName: "creative",
      //   title: "Creative",
      // },
      // {
      //   icon: "ActivitySquare",
      //   pageName: "dynamic",
      //   title: "Dynamic",
      // },
      // {
      //   icon: "Keyboard",
      //   pageName: "interactive",
      //   title: "Interactive",
      // },
      // "USER MANAGEMENT",
      // {
      //   icon: "SquareUser",
      //   pageName: "users",
      //   title: "Users",
      // },
      // {
      //   icon: "CakeSlice",
      //   pageName: "departments",
      //   title: "Departments",
      // },
      // {
      //   icon: "PackagePlus",
      //   pageName: "add-user",
      //   title: "Add User",
      // },
    ],
    dynamicMenu: []
  }),
  getters: {
    fullMenu(state): Array<Menu | SimpleMenu> {
      // Combine baseMenu with dynamicMenu
      const menu = [...state.baseMenu];

      // Find index of the "team-registration" pageName item
      const index = menu.findIndex(item => item.pageName === "team-registration");

      // Insert dynamicMenu items just before "team-registration"
      if (state.dynamicMenu.length > 0 && index !== -1) {
        menu.splice(index, 0, ...state.dynamicMenu);
      }

      return menu;
    }
  },
  actions: {
    updateDynamicMenu() {
      const authStore = useAuthStore();
      const teams = authStore.user.tournament_teams;

      // Reset dynamic menu
      this.dynamicMenu = [];

      if (teams && teams.length > 0) {
        if (teams.length === 1) {
          this.dynamicMenu.push({
            icon: "Dribbble",
            path: `/tournament/team/${teams[0].id}`,
            title: "View Your Team",
          });
        } else {
          this.dynamicMenu.push({
            icon: "Dribbble",
            path: `/tournament/teams`,
            title: "View Your Teams",
          });
          // this.dynamicMenu.push({
          //   icon: "Dribbble",
          //   path: `/teams`,
          //   title: "View Your Teams",
          // });
        }
      }
    }
  }
});
